import React, { useEffect, useState } from 'react';
import Inventory from './components/Inventory';
import InventoryMap from './components/InventoryMap';
import News from './components/News';
import { AppBar, Toolbar, Typography, Box, Drawer, List, ListItem, ListItemIcon, ListItemText, CssBaseline } from '@mui/material';
import ListIcon from '@mui/icons-material/List';
import MapIcon from '@mui/icons-material/Map';
import LayersIcon from '@mui/icons-material/Layers';
import NewspaperIcon from '@mui/icons-material/Newspaper';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import ManageGroups from './components/ManageGroups';
import { logScreenView } from './firebase';

// Black-and-white brand, one red accent reserved for the logo mark and a
// handful of "this matters" touches (active nav, delete actions, the News
// page's Latest badge) — never spread across every button.
export const COLORS = {
  black: '#0A0A0A',      // app background
  surface: '#161616',    // cards, forms, paper
  raised: '#000000',     // app bar / sidebar
  white: '#FFFFFF',      // primary text/icons
  grey: '#8F8F8F',       // secondary text, inactive icons
  border: '#2A2A2A',     // dividers, borders
  red: '#E10600',        // brand accent — speed, urgency
};

const darkTheme = createTheme({
  palette: {
    mode: 'dark',
    background: {
      default: COLORS.black,
      paper: COLORS.surface,
    },
    primary: {
      main: COLORS.white,
      contrastText: COLORS.black,
    },
    secondary: {
      main: COLORS.grey,
      contrastText: COLORS.black,
    },
    error: {
      main: COLORS.red,
      contrastText: COLORS.white,
    },
    text: {
      primary: COLORS.white,
      secondary: COLORS.grey,
      disabled: COLORS.grey,
    },
    divider: COLORS.border,
    action: {
      active: COLORS.white,
      hover: 'rgba(255,255,255,0.08)',
      selected: 'rgba(225,6,0,0.12)',
      focus: 'rgba(255,255,255,0.12)',
      disabled: COLORS.grey,
    },
  },
  typography: {
    fontFamily: 'Lato, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
    allVariants: {
      color: COLORS.white,
    },
  },
  components: {
    MuiAppBar: {
      styleOverrides: {
        root: {
          background: COLORS.raised,
          color: COLORS.white,
          boxShadow: 'none',
          borderBottom: `2px solid ${COLORS.red}`,
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          boxShadow: 'none',
          textTransform: 'none',
          fontWeight: 700,
        },
        containedPrimary: {
          color: COLORS.black,
          background: COLORS.white,
          '&:hover': {
            background: COLORS.grey,
            boxShadow: 'none',
          },
        },
        containedSecondary: {
          color: COLORS.white,
          background: COLORS.surface,
          border: `1px solid ${COLORS.border}`,
          '&:hover': {
            background: COLORS.border,
            boxShadow: 'none',
          },
        },
        outlined: {
          borderColor: COLORS.border,
          color: COLORS.white,
          '&:hover': {
            borderColor: COLORS.white,
            background: 'rgba(255,255,255,0.06)',
          },
        },
      },
    },
    MuiDrawer: {
      styleOverrides: {
        paper: {
          backgroundColor: COLORS.raised,
          borderRight: `1px solid ${COLORS.border}`,
          color: COLORS.white,
        },
      },
    },
    MuiListItemIcon: {
      styleOverrides: {
        root: {
          color: COLORS.white + ' !important',
        },
      },
    },
    MuiListItem: {
      styleOverrides: {
        root: {
          borderLeft: '3px solid transparent',
          '&.Mui-selected, &.Mui-selected:hover': {
            backgroundColor: 'rgba(225,6,0,0.1)',
            borderLeft: `3px solid ${COLORS.red}`,
            color: COLORS.red,
            '& .MuiListItemIcon-root': {
              color: COLORS.red + ' !important',
            },
          },
          '&:hover': {
            backgroundColor: 'rgba(255,255,255,0.06)',
            color: COLORS.white,
            '& .MuiListItemIcon-root': {
              color: COLORS.white + ' !important',
            },
          },
        },
      },
    },
    MuiInputLabel: {
      styleOverrides: {
        root: {
          color: COLORS.grey,
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          color: COLORS.white,
          background: COLORS.surface,
          borderColor: COLORS.border,
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: COLORS.white,
          },
        },
        notchedOutline: {
          borderColor: COLORS.border,
        },
        input: {
          color: COLORS.white,
        },
      },
    },
    MuiSelect: {
      styleOverrides: {
        icon: {
          color: COLORS.white,
        },
        select: {
          color: COLORS.white,
          background: COLORS.surface,
        },
      },
    },
    MuiMenuItem: {
      styleOverrides: {
        root: {
          color: COLORS.white,
          backgroundColor: COLORS.surface,
          '&.Mui-selected': {
            color: COLORS.white,
            backgroundColor: COLORS.border,
          },
          '&.Mui-selected:hover': {
            color: COLORS.white,
            backgroundColor: COLORS.border,
          },
          '&:hover': {
            color: COLORS.white,
            backgroundColor: 'rgba(255,255,255,0.06)',
          },
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          background: COLORS.surface,
          color: COLORS.white,
          borderColor: COLORS.border,
          marginBottom: '20px',
        },
      },
    },
    MuiFormControl: {
      styleOverrides: {
        root: {
          marginBottom: '20px',
        },
      },
    },
    MuiDivider: {
      styleOverrides: {
        root: {
          backgroundColor: COLORS.border,
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundColor: COLORS.surface,
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          fontWeight: 600,
        },
      },
    },
  },
});

const drawerWidth = 240;

const SCREEN_NAMES: Record<'list' | 'map' | 'groups' | 'news', string> = {
  list: 'Inventory List',
  map: 'Inventory Map',
  groups: 'Manage Groups',
  news: 'Industry News',
};

function App() {
  const [view, setView] = useState<'list' | 'map' | 'groups' | 'news'>('list');

  useEffect(() => {
    logScreenView(SCREEN_NAMES[view]);
  }, [view]);

  return (
    <ThemeProvider theme={darkTheme}>
      <CssBaseline />
      <Box sx={{ display: 'flex', bgcolor: 'background.default' }}>
        <AppBar position="fixed" sx={{ zIndex: (theme) => theme.zIndex.drawer + 1 }}>
          <Toolbar sx={{ gap: 1.5 }}>
            <Box
              sx={{
                width: 34,
                height: 34,
                borderRadius: '4px',
                bgcolor: COLORS.red,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                transform: 'skewX(-10deg)',
                flexShrink: 0,
              }}
            >
              <Typography
                sx={{
                  fontWeight: 900,
                  fontSize: '1.4rem',
                  lineHeight: 1,
                  color: COLORS.black,
                  fontStyle: 'italic',
                  transform: 'skewX(10deg)',
                }}
              >
                D
              </Typography>
            </Box>
            <Typography variant="h6" noWrap component="div" sx={{ flexGrow: 1, color: COLORS.white, fontWeight: 700, letterSpacing: 0.3 }}>
              OOHDash
            </Typography>
          </Toolbar>
        </AppBar>
        <Drawer
          variant="permanent"
          sx={{
            width: drawerWidth,
            flexShrink: 0,
            '& .MuiDrawer-paper': {
              width: drawerWidth,
              boxSizing: 'border-box',
            },
          }}
        >
          <Toolbar />
          <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', justifyContent: 'space-between', p: 0 }}>
            <List sx={{ flex: 1, p: 0 }}>
              <ListItem button onClick={() => setView('list')} selected={view === 'list'}>
                <ListItemIcon>
                  <ListIcon />
                </ListItemIcon>
                <ListItemText primary="Inventory List" />
              </ListItem>
              <ListItem button onClick={() => setView('map')} selected={view === 'map'}>
                <ListItemIcon>
                  <MapIcon />
                </ListItemIcon>
                <ListItemText primary="Inventory Map" />
              </ListItem>
              <ListItem button onClick={() => setView('groups')} selected={view === 'groups'}>
                <ListItemIcon>
                  <LayersIcon />
                </ListItemIcon>
                <ListItemText primary="Manage Groups" />
              </ListItem>
              <ListItem button onClick={() => setView('news')} selected={view === 'news'}>
                <ListItemIcon>
                  <NewspaperIcon />
                </ListItemIcon>
                <ListItemText primary="Industry News" />
              </ListItem>
            </List>
          </Box>
        </Drawer>
        <Box component="main" sx={{ flexGrow: 1, p: 0, bgcolor: 'background.default', minHeight: '100vh', height: '100vh', display: 'flex', flexDirection: 'column' }}>
          <Toolbar sx={{ minHeight: '64px' }} />
          <Box sx={{ flex: 1, minHeight: 0, p: 0, m: 0, width: '100%', height: '100%' }}>
            {view === 'list' ? <Inventory /> : view === 'map' ? <InventoryMap /> : view === 'news' ? <News /> : <ManageGroups />}
          </Box>
        </Box>
      </Box>
    </ThemeProvider>
  );
}

export default App;
